import { AuthService } from './authService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

export interface DeliveryMethodDto {
    id: number;
    companyNo: string;
    deliveryMethodNo: number;
    description: string | null;
    externalID: string | null;
    consignmentSWDefProductNo: string | null;
    consignmentSWAdditionalCode: string | null;
    shippingLabelsYesNo: boolean;
    shippingLabelsLayoutNo: number | null;
    labelExportFolder: string | null;
    labelExportFilename: string | null;
    packageSlipERPYesNo: boolean;
    shippingLabelsERPYesNo: boolean;
    consignmentNoteERPYesNo: boolean;
    singleParcelDelivery: boolean;
    updatedBy?: string;
    lastUpdate?: string;
    createdBy?: string;
    created?: string;
}

export interface CreateDeliveryMethodRequest {
    companyNo: string;
    deliveryMethodNo: number;
    description: string | null;
    externalID: string | null;
    consignmentSWDefProductNo: string | null;
    consignmentSWAdditionalCode: string | null;
    shippingLabelsYesNo: boolean;
    shippingLabelsLayoutNo: number | null;
    labelExportFolder: string | null;
    labelExportFilename: string | null;
    packageSlipERPYesNo: boolean;
    shippingLabelsERPYesNo: boolean;
    consignmentNoteERPYesNo: boolean;
    singleParcelDelivery: boolean;
}

export interface UpdateDeliveryMethodRequest {
    deliveryMethodNo?: number;
    description?: string | null;
    externalID?: string | null;
    consignmentSWDefProductNo?: string | null;
    consignmentSWAdditionalCode?: string | null;
    shippingLabelsYesNo?: boolean;
    shippingLabelsLayoutNo?: number | null;
    labelExportFolder?: string | null;
    labelExportFilename?: string | null;
    packageSlipERPYesNo?: boolean;
    shippingLabelsERPYesNo?: boolean;
    consignmentNoteERPYesNo?: boolean;
    singleParcelDelivery?: boolean;
}

export interface BulkUpdateDeliveryMethodRequest {
    companyNo: string;
    deliveryMethodNo: number;
    description: string | null;
    externalID: string | null;
    consignmentSWDefProductNo: string | null;
    consignmentSWAdditionalCode: string | null;
    shippingLabelsYesNo: boolean;
    shippingLabelsLayoutNo: number | null;
    labelExportFolder: string | null;
    labelExportFilename: string | null;
    packageSlipERPYesNo: boolean;
    shippingLabelsERPYesNo: boolean;
    consignmentNoteERPYesNo: boolean;
    singleParcelDelivery: boolean;
}

export interface BulkDeleteResult {
    deletedCount: number;
    requestedCount: number;
    notFoundIds: number[];
}

export interface LayoutLookupDto {
    layoutNo: number;
    description: string | null;
}

class DeliveryMethodService {
    private baseUrl = `${API_BASE_URL}/delivery-methods`;

    async getByDeliveryMethodNo(deliveryMethodNo: number): Promise<DeliveryMethodDto | null> {
        try {
            const response = await AuthService.authenticatedGet(`${this.baseUrl}/${deliveryMethodNo}`);
            if (!response.ok) {
                if (response.status === 404) return null;
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            return await response.json();
        } catch (error: any) {
            if (error.message?.includes('404')) return null;
            throw error;
        }
    }

    async getCurrent(): Promise<DeliveryMethodDto[]> {
        const response = await AuthService.authenticatedGet(`${this.baseUrl}/current`);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    async getAll(): Promise<DeliveryMethodDto[]> {
        const response = await AuthService.authenticatedGet(`${this.baseUrl}`);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    async create(request: CreateDeliveryMethodRequest): Promise<DeliveryMethodDto> {
        const response = await AuthService.authenticatedPost(`${this.baseUrl}`, request);
        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: response.statusText }));
            throw new Error(error.error || `HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    async update(deliveryMethodNo: number, request: UpdateDeliveryMethodRequest): Promise<DeliveryMethodDto> {
        const response = await AuthService.authenticatedPut(`${this.baseUrl}/${deliveryMethodNo}`, request);
        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: response.statusText }));
            throw new Error(error.error || `HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    async bulkUpdate(requests: BulkUpdateDeliveryMethodRequest[]): Promise<DeliveryMethodDto[]> {
        const response = await AuthService.authenticatedPut(`${this.baseUrl}/bulk`, requests);
        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: response.statusText }));
            throw new Error(error.error || `HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    async delete(deliveryMethodNo: number): Promise<void> {
        const response = await AuthService.authenticatedDelete(`${this.baseUrl}/${deliveryMethodNo}`);
        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: response.statusText }));
            throw new Error(error.error || `HTTP error! status: ${response.status}`);
        }
    }

    async bulkDelete(ids: number[]): Promise<BulkDeleteResult> {
        const response = await AuthService.authenticatedDelete(`${this.baseUrl}/bulk`, ids);
        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: response.statusText }));
            throw new Error(error.error || `HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    async exists(deliveryMethodNo: number): Promise<boolean> {
        try {
            const response = await AuthService.authenticatedGet(`${this.baseUrl}/${deliveryMethodNo}`);
            return response.ok;
        } catch {
            return false;
        }
    }

    async search(searchTerm: string): Promise<DeliveryMethodDto[]> {
        const response = await AuthService.authenticatedGet(`${this.baseUrl}/search?searchTerm=${encodeURIComponent(searchTerm)}`);
        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: response.statusText }));
            throw new Error(error.error || `HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    async getLayouts(): Promise<LayoutLookupDto[]> {
        try {
            const response = await AuthService.authenticatedGet(`${this.baseUrl}/layouts`);
            if (!response.ok) {
                console.warn('getLayouts failed:', response.status, response.statusText);
                return [];
            }
            const data = await response.json();
            return data;
        } catch (error) {
            console.warn('getLayouts error:', error);
            return [];
        }
    }
}

export const deliveryMethodService = new DeliveryMethodService();
